/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Product Metadata (file-based) Operations
 * 
 * Handles product.md file operations for repos.
 */

import type { ProductResponseData } from '@shared/types/responses/products';
import type { ProductMetadata, ProductImportResult, ProductSyncResult } from '@shared/types/products';
import { getProductServerBaseUrl } from './base';

/**
 * Check if product.md exists in a repository
 * Product server endpoint - uses direct fetch
 */
export async function checkProductMetadataExists(repoUrl: string, repoPath?: string | null): Promise<{
  exists: boolean;
  repoCloned: boolean;
  path?: string;
}> {
  try {
    const params = new URLSearchParams({ repoUrl });
    if (repoPath) {
      params.set('repoPath', repoPath);
    }

    const response = await fetch(`${getProductServerBaseUrl()}/api/product-metadata/check?${params}`);
    const result = await response.json();

    if (result.success) {
      return result.data;
    }

    return { exists: false, repoCloned: false };
  } catch (error: any) {
    console.error('[SkillShop Service] Error checking product metadata:', error);
    return { exists: false, repoCloned: false };
  }
}

/**
 * Read product metadata from a repository's product.md file
 * Product server endpoint - uses direct fetch
 */
export async function readProductMetadata(repoUrl: string, repoPath?: string | null): Promise<ProductMetadata | null> {
  try {
    const params = new URLSearchParams({ repoUrl });
    if (repoPath) {
      params.set('repoPath', repoPath);
    }

    const response = await fetch(`${getProductServerBaseUrl()}/api/product-metadata?${params}`);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorResult = await response.json();
      throw new Error(errorResult.error || `Failed to read product metadata (${response.status})`);
    }

    const result = await response.json();
    return result.data as ProductMetadata;
  } catch (error: any) {
    console.error('[SkillShop Service] Error reading product metadata:', error);
    throw error;
  }
}

/**
 * Write product metadata to a repository's product.md file
 * Product server endpoint - uses direct fetch
 */
export async function writeProductMetadata(
  repoUrl: string,
  product: ProductMetadata,
  repoPath?: string | null,
  commitMessage?: string
): Promise<ProductMetadata> {
  try {
    const response = await fetch(`${getProductServerBaseUrl()}/api/product-metadata`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repoUrl,
        repoPath: repoPath || undefined,
        product,
        commitMessage,
      }),
    });

    if (!response.ok) {
      const errorResult = await response.json();
      throw new Error(errorResult.error || `Failed to write product metadata (${response.status})`);
    }

    const result = await response.json();
    return result.data as ProductMetadata;
  } catch (error: any) {
    console.error('[SkillShop Service] Error writing product metadata:', error);
    throw error;
  }
}

/**
 * Import a product from a repository
 * Clones the repo if needed, reads product.md, handles org access, and syncs to database
 * Product server endpoint - uses direct fetch
 * 
 * Philosophy: Git repo access is sufficient authorization. If a user can access the
 * repository, they can import the product. When the product belongs to a different
 * organization, the user is auto-added to that organization.
 * 
 * @param repoUrl - Repository URL to import from
 * @param userOrganizationId - Current user's organization ID
 * @param repoPath - Optional path within monorepo
 * @param accessToken - Optional access token for cloning private repos
 * @param useSSH - Whether to use SSH for cloning
 * @param autoCreate - If true, auto-create product when product.md doesn't exist
 * @param userId - User ID for auto-joining organizations on import
 */
export async function importProductFromRepo(
  repoUrl: string,
  userOrganizationId: string,
  repoPath?: string | null,
  accessToken?: string,
  useSSH?: boolean,
  autoCreate?: boolean,
  userId?: number
): Promise<ProductImportResult> {
  try {
    console.log('[SkillShop Service] Importing product from:', repoUrl, useSSH ? '(SSH)' : '', autoCreate ? '(autoCreate)' : '');

    const response = await fetch(`${getProductServerBaseUrl()}/api/product-metadata/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repoUrl,
        repoPath: repoPath || undefined,
        accessToken,
        userOrganizationId,
        userId,
        useSSH: useSSH || false,
        autoCreate: autoCreate || false,
      }),
    });

    if (!response.ok) {
      const errorResult = await response.json();
      throw new Error(errorResult.error || `Failed to import product (${response.status})`);
    }

    const result = await response.json();
    return result.data as ProductImportResult;
  } catch (error: any) {
    console.error('[SkillShop Service] Error importing product:', error);
    return {
      success: false,
      error: error.message || 'Failed to import product',
    };
  }
}

/**
 * Sync a product from its repository file to the database
 * Called after git sync operations to update the database cache
 * Product server endpoint - uses direct fetch
 */
export async function syncProductFromFile(
  productId: string,
  repoUrl: string,
  repoPath?: string | null
): Promise<ProductSyncResult> {
  try {
    console.log('[SkillShop Service] Syncing product from file:', productId);

    const response = await fetch(`${getProductServerBaseUrl()}/api/product-metadata/${productId}/sync-from-file`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repoUrl,
        repoPath: repoPath || undefined,
      }),
    });

    if (!response.ok) {
      const errorResult = await response.json();
      throw new Error(errorResult.error || `Failed to sync product (${response.status})`);
    }

    const result = await response.json();
    return result.data as ProductSyncResult;
  } catch (error: any) {
    console.error('[SkillShop Service] Error syncing product from file:', error);
    return {
      success: false,
      error: error.message || 'Failed to sync product',
    };
  }
}

/**
 * Sync product metadata when repository URL changes
 * 
 * This handles the bidirectional sync between database and file:
 * - If product.md exists in the repo: sync FROM file TO database (file wins)
 * - If product.md doesn't exist: write FROM database TO file (database wins)
 */
export async function syncProductMetadataOnRepoChange(
  productId: string,
  product: ProductResponseData,
  repoUrl: string,
  repoPath?: string | null
): Promise<{
  success: boolean;
  action: 'synced_from_file' | 'written_to_file' | 'no_action';
  syncedProduct?: ProductMetadata;
  error?: string;
}> {
  try {
    console.log('[SkillShop Service] Syncing product metadata on repo change:', {
      productId,
      repoUrl,
      repoPath
    });

    // Step 1: Check if product.md exists in the repository
    const metadataCheck = await checkProductMetadataExists(repoUrl, repoPath);
    
    console.log('[SkillShop Service] Product metadata check result:', metadataCheck);

    if (!metadataCheck.repoCloned) {
      return {
        success: false,
        action: 'no_action',
        error: 'Repository not cloned. Ensure the repo is cloned first.'
      };
    }

    if (metadataCheck.exists) {
      // product.md exists - sync FROM file TO database
      console.log('[SkillShop Service] product.md exists, syncing from file to database');
      
      const syncResult = await syncProductFromFile(productId, repoUrl, repoPath);
      
      if (syncResult.success) {
        const syncedMetadata = await readProductMetadata(repoUrl, repoPath);
        
        return {
          success: true,
          action: 'synced_from_file',
          syncedProduct: syncedMetadata || undefined
        };
      } else {
        return {
          success: false,
          action: 'synced_from_file',
          error: syncResult.error || 'Failed to sync from file'
        };
      }
    } else {
      // product.md doesn't exist - write FROM database TO file
      console.log('[SkillShop Service] product.md does not exist, writing database to file');
      
      const metadata: ProductMetadata = {
        id: product.id,
        productId: product.productId,
        organizationId: product.organizationId,
        title: product.title,
        description: product.description,
        owner: product.owner,
        repositoryUrl: repoUrl,
        repoPath: repoPath ?? null,
        capabilities: product.capabilities || [],
        users: product.users || [],
        links: product.links || [],
        architectureDiagram: product.architectureDiagram ?? null,
        createdAt: typeof product.createdAt === 'string' 
          ? product.createdAt 
          : new Date(product.createdAt).toISOString(),
        updatedAt: typeof product.updatedAt === 'string' 
          ? product.updatedAt 
          : new Date(product.updatedAt).toISOString(),
      };

      const writtenMetadata = await writeProductMetadata(
        repoUrl,
        metadata,
        repoPath,
        `Initialize product metadata: ${product.title}`
      );

      return {
        success: true,
        action: 'written_to_file',
        syncedProduct: writtenMetadata
      };
    }
  } catch (error: any) {
    console.error('[SkillShop Service] Error syncing product metadata on repo change:', error);
    return {
      success: false,
      action: 'no_action',
      error: error.message || 'Failed to sync product metadata'
    };
  }
}

